import { Observation } from '@medplum/fhirtypes'
import { Card, CardContent, Typography, useTheme } from '@mui/material'
import { LineChart } from '@mui/x-charts'
import { format } from 'date-fns'
import { GradientAreaFill } from './GradientAreaFill'

type LabResultsHistoryLineChartProps = {
    testResultHistory: Observation[]
    title?: string
}

export const LabResultsHistoryLineChart = ({
    testResultHistory,
    title
}: LabResultsHistoryLineChartProps) => {
    const theme = useTheme()

    // Extract dates and values
    const dates = testResultHistory.map(result =>
        format(new Date(result.effectiveDateTime || ''), 'MMM d')
    )

    const values = testResultHistory.map(
        result => result.valueQuantity?.value || 0
    )

    // Adjust max Y value based on data
    const maxValue = Math.max(...values)
    const yMax = Math.ceil(maxValue / 10) * 10 + 10

    return (
        <Card
            elevation={0}
            sx={{
                border: 1,
                borderColor: 'divider',
                mt: 2
            }}
        >
            <CardContent sx={{ px: 4 }}>
                {title && (
                    <Typography
                        variant="h6"
                        sx={{
                            fontWeight: 600,
                            mb: 2
                        }}
                    >
                        {title}
                    </Typography>
                )}
                <LineChart
                    xAxis={[
                        {
                            data: dates,
                            scaleType: 'point',
                            tickLabelStyle: {
                                fontSize: 12,
                                fill: theme.palette.text.secondary
                            }
                        }
                    ]}
                    yAxis={[
                        {
                            min: 0,
                            max: yMax,
                            tickLabelStyle: {
                                fontSize: 12,
                                fill: theme.palette.text.secondary
                            }
                        }
                    ]}
                    series={[
                        {
                            data: values,
                            curve: 'linear',
                            area: true,
                            // we don't have this color in the theme, so we used the hex code
                            color: '#D67360',
                            showMark: false
                        }
                    ]}
                    height={300}
                    margin={{ left: 50, right: 20, top: 20, bottom: 30 }}
                    sx={{
                        '.MuiLineElement-root': {
                            strokeWidth: 2
                        },
                        '.MuiAreaElement-root': {
                            fill: 'url(#areaGradient)'
                        },
                        '.MuiChartsAxis-line, .MuiChartsAxis-tick': {
                            stroke: theme.palette.text.secondary
                        },
                        '.MuiChartsAxis-line': {
                            stroke: theme.palette.text.secondary
                        }
                    }}
                >
                    <GradientAreaFill />
                </LineChart>
            </CardContent>
        </Card>
    )
}
